import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { Tab } from '../../services/tab/tab.service';
import { Tabbing } from '../tabbing/tabbing.page';
import { TabsState } from '../../store/tab/tab.reducer';
import { LyftState } from '../../store/lyft/lyft.reducer';
import * as LyftSelectors from '../../store/lyft/lyft.selectors';
import * as LyftActions from '../../store/lyft/lyft.actions';
import { PageEvent } from '@angular/material/paginator';
import { LyftDispatchRideStatus, LyftDispatchRideTracker } from '@uzurv/clara';
import { APP_CONFIGURATION, AppConfiguration } from '../../app/app-configuration';
import { takeUntil, withLatestFrom } from 'rxjs';
import { isNullOrUndefined } from '@uzurv/core';

export interface RideList {
  loading: boolean;
  rides: LyftDispatchRideTracker[] | null;
  pageSizeOptions?: number[];
  paginationLength?: number;
  statuses: LyftDispatchRideStatus[];
  refreshIntervalId?: number;
}

export const LYFT_PARAMS_KEY = 'lyftParams';
export interface LyftParams {
  ride?: number | null;
  pageIndex?: number;
  pageSize?: number;
  search?: string;
  filters?: number[];
}

@Component({
  selector: 'app-lyft-page',
  templateUrl: './lyft.page.html',
  styleUrls: ['./lyft.page.scss']
})
export class LyftPage extends Tabbing<LyftState> implements OnInit, OnDestroy {
  rideList$ = this.store$.select(LyftSelectors.getRideList);
  rideDetails$ = this.store$.select(LyftSelectors.getRideDetails);
  isLyftListLoading$ = this.store$.select(LyftSelectors.isLyftListLoading);
  lyftParams$ = this.store$.select(LyftSelectors.getLyftParams);

  refreshInterval: number;

  constructor(
    protected readonly tabStore$: Store<TabsState>,
    private store$: Store,
    @Inject(APP_CONFIGURATION) private appConfiguration: AppConfiguration
  ) {
    super(tabStore$);
  }

  ngOnInit() {
    this.refreshInterval = window.setInterval(() => {
      this.store$.dispatch(LyftActions.RefreshLyftRides());
    }, this.appConfiguration.listPollingSeconds * 1000);

    this.lyftParams$.pipe(takeUntil(this.destroyed$), withLatestFrom(this.rideDetails$)).subscribe(([lyftParams, rideDetails]) => {
      if (!isNullOrUndefined(rideDetails.ride?.rideRequestId) && rideDetails.ride?.rideRequestId !== lyftParams.ride) {
        this.store$.dispatch(LyftActions.FetchLyftRideDetails({ ride: lyftParams.ride ? lyftParams.ride : rideDetails.ride?.rideRequestId }));
      }
    });
  }

  ngOnDestroy() {
    window.clearInterval(this.refreshInterval);
    super.ngOnDestroy();
  }

  handleRideSelected(ride: LyftDispatchRideTracker) {
    this.store$.dispatch(LyftActions.SelectRide({ ride }));
  }

  handlePaginationChanged(pageEvent: PageEvent) {
    this.store$.dispatch(LyftActions.SetPagination({ pagination: pageEvent }));
  }

  handleSearchChanged(search: string) {
    this.store$.dispatch(LyftActions.SetSearch({ search }));
  }

  handleFilterChanged(filters: number[]) {
    this.store$.dispatch(LyftActions.SetFilters({ filters }));
  }

  getTabbingConfig(): Tab<LyftState, typeof LYFT_PARAMS_KEY> {
    return {
      name: 'Lyft',
      matIcon: 'extension',
      selectorState: LyftSelectors.selectLyftState,
      initializeActionOnCacheLoaded: true,
      queryParamConfig: {
        queryParamStateKey: LYFT_PARAMS_KEY,
        queryParamsAction: LyftActions.SetLyftParams,
        queryParamDefaults: {
          pageIndex: 0,
          pageSize: 20
        }
      },
      initializeAction: tabConfig => LyftActions.Start(tabConfig),
      closeTabAction: () => LyftActions.Closed(),
      restoreSnapshotAction: snapshot => LyftActions.RestoreSnapshot({ state: snapshot })
    };
  }
}
